'use client'
import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { registrationSchema, type RegistrationFormData } from '@/lib/validations'
import { calculateAge } from '@/lib/utils'
import { toast } from 'sonner'
import { CheckCircle2, Loader2, User, Phone, MapPin, Calendar, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useLanguage } from '@/lib/language-context'

function FormSection({ icon: Icon, title, children }: { icon: React.ElementType, title: string, children: React.ReactNode }) {
  return (
    <Card className="border-0 shadow-md overflow-hidden">
      <CardHeader className="py-3 px-5" style={{ background: 'linear-gradient(135deg, #4A2E14, #E67E22)' }}>
        <CardTitle className="flex items-center gap-2 text-white text-base">
          <Icon className="h-4 w-4" style={{ color: '#FFF7EE' }} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 bg-white">{children}</CardContent>
    </Card>
  )
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{message}</p>
}

interface SuccessState {
  registration_id: string
}

export function RegistrationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState<SuccessState | null>(null)
  const [whatsappSame, setWhatsappSame] = useState(false)
  const [age, setAge] = useState<number | null>(null)
  const { t } = useLanguage()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
  })

  const watchedMobile = watch('mobile_no')
  const watchedDob = watch('dob')

  // Auto-calculate age from DOB
  useEffect(() => {
    if (watchedDob) {
      try {
        const calculatedAge = calculateAge(watchedDob)
        setAge(calculatedAge)
      } catch { setAge(null) }
    } else {
      setAge(null)
    }
  }, [watchedDob])

  // WhatsApp same as mobile
  useEffect(() => {
    if (whatsappSame && watchedMobile) {
      setValue('whatsapp_no', watchedMobile, { shouldValidate: true })
    }
  }, [whatsappSame, watchedMobile, setValue])

  const onSubmit = useCallback(async (data: RegistrationFormData) => {
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const json = await res.json()
      if (!res.ok) {
        if (json.field === 'mobile_no') setError('mobile_no', { message: json.error })
        else if (json.field === 'whatsapp_no') setError('whatsapp_no', { message: json.error })
        else toast.error(json.error || 'Registration failed')
        return
      }
      setSuccess({ registration_id: json.registration_id })
      toast.success(t('registrationSuccessful'))
    } catch {
      toast.error('Network error. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [setError, t])

  if (success) {
    return (
      <div className="max-w-lg mx-auto">
        <Card className="border-0 shadow-2xl overflow-hidden">
          <div className="p-8 text-center" style={{ background: 'linear-gradient(135deg, #4A2E14 0%, #E67E22 100%)' }}>
            <div className="success-pop">
              <CheckCircle2 className="h-20 w-20 mx-auto mb-4" style={{ color: '#FFF7EE' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{t('registrationSuccessful')}</h2>
            <p className="text-white/80 mb-6">{t('registrationSuccessMsg')}</p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 mb-6">
              <p className="text-white/70 text-sm mb-1">{t('yourRegistrationId')}</p>
              <p className="text-3xl font-bold tracking-widest" style={{ color: '#FFF7EE' }}>{success.registration_id}</p>
            </div>
            <p className="text-white/60 text-sm">{t('saveIdNote')}</p>
          </div>
          <div className="p-6 bg-white text-center">
            <Button onClick={() => { setSuccess(null); window.location.reload() }}
              className="px-8" style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)', color: 'white' }}>
              {t('registerAnother')}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  const inputClass = "border-gray-200 focus:border-[#E67E22] focus:ring-[#E67E22]/20 bg-white"
  const selectClass = "border-gray-200 focus:border-[#E67E22] w-full h-10 rounded-md px-3 text-sm bg-white"

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Personal Information */}
      <FormSection icon={User} title={t('personalInfo')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">
              {t('firstName')} <span className="text-red-500">*</span>
            </Label>
            <Input id="first_name" {...register('first_name')} className={`mt-1 ${inputClass}`} placeholder={t('firstName')} />
            <FieldError message={errors.first_name?.message} />
          </div>
          <div>
            <Label htmlFor="middle_name" className="text-sm font-medium text-gray-700">{t('middleName')}</Label>
            <Input id="middle_name" {...register('middle_name')} className={`mt-1 ${inputClass}`} placeholder={t('middleName')} />
          </div>
          <div>
            <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">
              {t('lastName')} <span className="text-red-500">*</span>
            </Label>
            <Input id="last_name" {...register('last_name')} className={`mt-1 ${inputClass}`} placeholder={t('lastName')} />
            <FieldError message={errors.last_name?.message} />
          </div>
          <div>
            <Label htmlFor="gender" className="text-sm font-medium text-gray-700">
              {t('gender')} <span className="text-red-500">*</span>
            </Label>
            <select id="gender" {...register('gender')} className={`mt-1 ${selectClass} border`}>
              <option value="">{t('selectGender')}</option>
              <option value="Male">{t('male')}</option>
              <option value="Female">{t('female')}</option>
              <option value="Other">{t('other')}</option>
            </select>
            <FieldError message={errors.gender?.message} />
          </div>
          <div>
            <Label htmlFor="blood_group" className="text-sm font-medium text-gray-700">
              {t('bloodGroup')}
            </Label>
            <select id="blood_group" {...register('blood_group')} className={`mt-1 ${selectClass} border`}>
              <option value="">{t('selectBloodGroup')}</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>
        </div>
      </FormSection>

      {/* Contact Details */}
      <FormSection icon={Phone} title={t('contactDetails')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="mobile_no" className="text-sm font-medium text-gray-700">
              {t('mobileNumber')} <span className="text-red-500">*</span>
            </Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</span>
              <Input id="mobile_no" {...register('mobile_no')} className={`rounded-l-none ${inputClass}`} placeholder="9XXXXXXXXX" maxLength={10} />
            </div>
            <FieldError message={errors.mobile_no?.message} />
          </div>
          <div>
            <Label htmlFor="whatsapp_no" className="text-sm font-medium text-gray-700">
              {t('whatsappNumber')} <span className="text-red-500">*</span>
            </Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</span>
              <Input id="whatsapp_no" {...register('whatsapp_no')} readOnly={whatsappSame}
                className={`rounded-l-none ${inputClass} ${whatsappSame ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                placeholder="9XXXXXXXXX" maxLength={10} />
            </div>
            <FieldError message={errors.whatsapp_no?.message} />
            <div className="flex items-center gap-2 mt-2">
              <Checkbox id="whatsapp_same" checked={whatsappSame}
                onCheckedChange={(checked) => setWhatsappSame(!!checked)} />
              <Label htmlFor="whatsapp_same" className="text-xs text-gray-600 cursor-pointer">
                {t('sameAsMobile')}
              </Label>
            </div>
          </div>
        </div>
      </FormSection>

      {/* Address */}
      <FormSection icon={MapPin} title={t('addressInfo')}>
        <div className="mb-4">
          <Label className="text-sm font-medium text-gray-700">{t('fullAddress')}</Label>
          <Textarea {...register('full_address')} className={`mt-1 ${inputClass}`}
            placeholder={t('fullAddressPlaceholder')} rows={3} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('state')} <span className="text-red-500">*</span></Label>
            <Input {...register('state')} defaultValue="Maharashtra" readOnly className={`mt-1 ${inputClass} bg-gray-50 cursor-not-allowed`} />
            <FieldError message={errors.state?.message} />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('district')} <span className="text-red-500">*</span></Label>
            <Input {...register('district')} defaultValue="Pune" readOnly className={`mt-1 ${inputClass} bg-gray-50 cursor-not-allowed`} />
            <FieldError message={errors.district?.message} />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('wardNumber')}</Label>
            <Input {...register('ward_number')} className={`mt-1 ${inputClass}`} placeholder={t('wardPlaceholder')} />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('pincode')}</Label>
            <Input {...register('pincode')} className={`mt-1 ${inputClass}`} placeholder={t('pincodePlaceholder')} maxLength={6} />
            <FieldError message={errors.pincode?.message} />
          </div>
        </div>
      </FormSection>

      {/* Birth Information */}
      <FormSection icon={Calendar} title={t('birthInfo')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="dob" className="text-sm font-medium text-gray-700">
              {t('dob')} <span className="text-red-500">*</span>
            </Label>
            <Input id="dob" type="date" {...register('dob')} className={`mt-1 ${inputClass}`}
              max={new Date().toISOString().split('T')[0]} />
            <FieldError message={errors.dob?.message} />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('age')}</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="flex-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 flex items-center">
                {age !== null ? (
                  <span className="font-bold text-lg" style={{ color: '#8B1E3F' }}>{age} <span className="font-normal text-sm text-gray-500">{t('years')}</span></span>
                ) : (
                  <span className="text-gray-400 text-sm">{t('ageSelect')}</span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('ageNote')}</p>
          </div>
        </div>
      </FormSection>

      {/* Submit */}
      <div className="flex justify-center pt-2">
        <Button type="submit" disabled={isSubmitting}
          className="px-12 py-3 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)', color: 'white' }}>
          {isSubmitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('submittingRegistration')}</>
          ) : (
            t('submitRegistration')
          )}
        </Button>
      </div>
      <p className="text-center text-xs text-gray-500">{t('requiredNote')}</p>
    </form>
  )
}
